"use client";

import { c as _c } from "react/compiler-runtime";
import { useChat } from "ai/react";
import { Send, X, MessageCircleIcon, Loader2, RefreshCw, Trash2, Copy, Check, AlertCircle, ChevronDown, ExternalLink, FileText } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// ── localStorage history ──────────────────────────────────────────────
const STORAGE_KEY = "routerbench-ask-ai-history";
const SOURCES_KEY = "routerbench-ask-ai-sources";
const MAX_STORED_MESSAGES = 50;
function loadHistory() {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-MAX_STORED_MESSAGES) : [];
  } catch {
    return [];
  }
}
function saveHistory(messages: any[]) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)));
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
}
interface Source {
  title: string;
  url: string;
}
function loadSources(): Record<string, Source[]> {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(SOURCES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveSources(sources: Record<string, Source[]>) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(SOURCES_KEY, JSON.stringify(sources));
  } catch {
    localStorage.removeItem(SOURCES_KEY);
  }
}

// ── Copy button ───────────────────────────────────────────────────────
function CopyButton(t0) {
  const $ = _c(7);
  const {
    text
  } = t0;
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== text) {
    t1 = () => {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== copied) {
    t2 = copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />;
    $[2] = copied;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <button onClick={t1} className="absolute right-2 top-2 rounded-md p-1.5 text-fd-muted-foreground opacity-0 transition-opacity hover:bg-fd-accent hover:text-fd-accent-foreground group-hover/code:opacity-100" title="Copy code">{t2}</button>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// ── Source citations ──────────────────────────────────────────────────
function SourceCitations(t0) {
  const $ = _c(4);
  const {
    sources
  } = t0;
  if (!sources || sources.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== sources) {
    t1 = sources.map(_temp);
    $[0] = sources;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="mt-3 flex flex-wrap gap-1.5">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

// ── Markdown renderer ─────────────────────────────────────────────────
function _temp(s, i) {
  return <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-fd-border bg-fd-muted/50 px-2.5 py-1.5 text-[11px] text-fd-muted-foreground hover:bg-fd-accent hover:text-fd-accent-foreground transition-colors"><FileText className="size-3 shrink-0" /><span className="truncate max-w-[180px]">{s.title}</span><ExternalLink className="size-2.5 shrink-0 opacity-50" /></a>;
}
function AiMarkdown(t0) {
  const $ = _c(4);
  const {
    content
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [remarkGfm];
    t2 = {
      h1: _temp2,
      h2: _temp3,
      h3: _temp4,
      p: _temp5,
      a: _temp6,
      strong: _temp7,
      em: _temp8,
      blockquote: _temp9,
      ul: _temp10,
      ol: _temp11,
      li: _temp12,
      hr: _temp13,
      table: _temp14,
      thead: _temp15,
      th: _temp16,
      td: _temp17,
      code: _temp18,
      pre: _temp19
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== content) {
    t3 = <ReactMarkdown remarkPlugins={t1} components={t2}>{content}</ReactMarkdown>;
    $[2] = content;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

// ── Main component ────────────────────────────────────────────────────
function _temp19(t0) {
  const {
    children: children_15
  } = t0;
  return children_15;
}
function _temp18(t0) {
  const {
    className,
    children: children_14
  } = t0;
  const match = /language-(\w+)/.exec(className || "");
  const codeString = String(children_14).replace(/\n$/, "");
  if (!match && !codeString.includes("\n")) {
    return <code className="rounded bg-fd-muted px-1.5 py-0.5 text-xs font-mono">{children_14}</code>;
  }
  return <div className="group/code relative my-2">{match && <div className="flex items-center justify-between rounded-t-lg border border-b-0 border-fd-border bg-fd-muted/50 px-3 py-1"><span className="text-[10px] font-medium uppercase tracking-wider text-fd-muted-foreground">{match[1]}</span></div>}<pre className={`overflow-x-auto bg-fd-muted p-3 text-xs font-mono leading-relaxed ${match ? "rounded-b-lg border border-t-0 border-fd-border" : "rounded-lg border border-fd-border"}`}><code>{codeString}</code></pre><CopyButton text={codeString} /></div>;
}
function _temp17(t0) {
  const {
    children: children_13
  } = t0;
  return <td className="border-t border-fd-border px-3 py-1.5">{children_13}</td>;
}
function _temp16(t0) {
  const {
    children: children_12
  } = t0;
  return <th className="px-3 py-1.5 text-left font-medium">{children_12}</th>;
}
function _temp15(t0) {
  const {
    children: children_11
  } = t0;
  return <thead className="bg-fd-muted/50">{children_11}</thead>;
}
function _temp14(t0) {
  const {
    children: children_10
  } = t0;
  return <div className="my-2 overflow-x-auto rounded-lg border border-fd-border"><table className="w-full text-xs">{children_10}</table></div>;
}
function _temp13() {
  return <hr className="my-3 border-fd-border" />;
}
function _temp12(t0) {
  const {
    children: children_9
  } = t0;
  return <li className="leading-relaxed">{children_9}</li>;
}
function _temp11(t0) {
  const {
    children: children_8
  } = t0;
  return <ol className="my-1.5 ml-4 list-decimal space-y-0.5">{children_8}</ol>;
}
function _temp10(t0) {
  const {
    children: children_7
  } = t0;
  return <ul className="my-1.5 ml-4 list-disc space-y-0.5">{children_7}</ul>;
}
function _temp9(t0) {
  const {
    children: children_6
  } = t0;
  return <blockquote className="my-2 border-l-2 border-fd-primary/40 pl-3 text-fd-muted-foreground italic">{children_6}</blockquote>;
}
function _temp8(t0) {
  const {
    children: children_5
  } = t0;
  return <em className="italic">{children_5}</em>;
}
function _temp7(t0) {
  const {
    children: children_4
  } = t0;
  return <strong className="font-semibold">{children_4}</strong>;
}
function _temp6(t0) {
  const {
    href,
    children: children_3
  } = t0;
  return <a href={href} target="_blank" rel="noopener noreferrer" className="text-fd-primary underline underline-offset-2 hover:text-fd-primary/80">{children_3}</a>;
}
function _temp5(t0) {
  const {
    children: children_2
  } = t0;
  return <p className="my-1.5 leading-relaxed">{children_2}</p>;
}
function _temp4(t0) {
  const {
    children: children_1
  } = t0;
  return <h3 className="mt-2 mb-1 text-sm font-semibold">{children_1}</h3>;
}
function _temp3(t0) {
  const {
    children: children_0
  } = t0;
  return <h2 className="mt-3 mb-1.5 text-base font-semibold">{children_0}</h2>;
}
function _temp2(t0) {
  const {
    children
  } = t0;
  return <h1 className="mt-4 mb-2 text-lg font-bold">{children}</h1>;
}
export function AskAI() {
  const $ = _c(75);
  const [isOpen, setIsOpen] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [sourcesMap, setSourcesMap] = useState(loadSources);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = loadHistory();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      api: "/api/chat",
      initialMessages: t0,
      onError: _temp20,
      onResponse: response => {
        const sourcesHeader = response.headers.get("X-Sources");
        if (sourcesHeader) {
          try {
            const sources = JSON.parse(decodeURIComponent(sourcesHeader));
            if (sources.length > 0) {
              setSourcesMap(prev => {
                const updated = {
                  ...prev,
                  __pending__: sources
                };
                saveSources(updated);
                return updated;
              });
            }
          } catch {}
        }
      },
      onFinish: message => {
        setSourcesMap(prev_0 => {
          if (prev_0.__pending__) {
            const updated_0 = {
              ...prev_0,
              [message.id]: prev_0.__pending__
            };
            delete updated_0.__pending__;
            saveSources(updated_0);
            return updated_0;
          }
          return prev_0;
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    messages,
    input,
    handleInputChange,
    handleSubmit: rawHandleSubmit,
    isLoading,
    setMessages,
    reload,
    stop,
    error
  } = useChat(t1);
  let t2;
  let t3;
  if ($[2] !== messages) {
    t2 = () => {
      if (messages.length > 0) {
        saveHistory(messages);
      }
    };
    t3 = [messages];
    $[2] = messages;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== messages) {
    t5 = [messages];
    $[6] = messages;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== isOpen) {
    t6 = () => {
      if (isOpen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    };
    t7 = [isOpen];
    $[8] = isOpen;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] !== isOpen) {
    t8 = () => {
      const handler = e => {
        if ((e.metaKey || e.ctrlKey) && e.key === "/") {
          e.preventDefault();
          setIsOpen(_temp21);
        }
        if (e.key === "Escape" && isOpen) {
          setIsOpen(false);
        }
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    };
    t9 = [isOpen];
    $[11] = isOpen;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const el = scrollContainerRef.current;
      if (!el) {
        return;
      }
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      setShowScrollDown(!atBottom);
    };
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const handleScroll = t10;
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const scrollToBottom = t11;
  let t12;
  if ($[16] !== input || $[17] !== isLoading || $[18] !== rawHandleSubmit) {
    t12 = e_0 => {
      if (!e_0.shiftKey && e_0.key === "Enter") {
        e_0.preventDefault();
        if (input.trim() && !isLoading) {
          rawHandleSubmit(e_0 as any);
        }
      }
    };
    $[16] = input;
    $[17] = isLoading;
    $[18] = rawHandleSubmit;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const onKeyDown = t12;
  let t13;
  if ($[20] !== setMessages) {
    t13 = () => {
      setMessages([]);
      setSourcesMap({});
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SOURCES_KEY);
    };
    $[20] = setMessages;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const clearChat = t13;
  if (!isOpen) {
    let t14;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = () => setIsOpen(true);
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    let t15;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <button onClick={t14} className="fixed right-4 bottom-4 md:right-6 md:bottom-6 z-40 flex items-center gap-2 rounded-full border border-fd-border bg-fd-primary px-3 py-2 md:px-4 md:py-2.5 text-xs md:text-sm font-medium text-fd-primary-foreground shadow-lg transition-all hover:shadow-xl hover:scale-105 active:scale-95" title="Ask AI (Cmd+/)"><MessageCircleIcon className="size-4" />Ask AI</button>;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    return t15;
  }
  let t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex-1"><p className="text-sm font-medium">Ask AI</p><p className="text-[11px] text-fd-muted-foreground">Powered by RouterBench</p></div>;
    t15 = () => setIsOpen(false);
    $[24] = t14;
    $[25] = t15;
  } else {
    t14 = $[24];
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="flex items-center gap-2 border-b border-fd-border bg-fd-secondary px-4 py-3">{t14}<button onClick={t15} className="rounded-full p-1.5 text-fd-muted-foreground hover:bg-fd-accent hover:text-fd-accent-foreground transition-colors" title="Close (Esc)"><X className="size-4" /></button></div>;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== error || $[28] !== handleInputChange || $[29] !== messages.length) {
    t17 = messages.length === 0 && !error && <div className="flex flex-col items-center justify-center h-full text-center text-fd-muted-foreground px-4"><MessageCircleIcon className="size-8 mb-3 opacity-40" /><p className="text-sm font-medium mb-1">Ask anything about RouterBench</p><p className="text-xs mb-6">AI searches the docs to give accurate answers with citations</p><div className="flex flex-wrap gap-2 justify-center">{["How do I get started?", "How does the embeddings API work?", "What audio models are supported?", "How does cost tracking work?", "Show me a Python SDK example", "What is smart routing?"].map(q => <button key={q} onClick={() => {
          handleInputChange({
            target: {
              value: q
            }
          } as any);
          setTimeout(_temp22, 50);
        }} className="rounded-xl border border-fd-border px-3 py-2 text-xs hover:bg-fd-accent hover:text-fd-accent-foreground transition-colors text-left">{q}</button>)}</div></div>;
    $[27] = error;
    $[28] = handleInputChange;
    $[29] = messages.length;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== messages || $[32] !== sourcesMap) {
    let t19;
    if ($[34] !== sourcesMap) {
      t19 = m => <div key={m.id} role="article"><span className="text-[11px] font-medium text-fd-muted-foreground uppercase tracking-wide">{m.role === "user" ? "You" : "RouterBench"}</span>{m.role === "user" ? <div className="mt-1 rounded-lg bg-fd-secondary px-3 py-2 text-sm">{m.content}</div> : <div className="mt-1 text-sm leading-relaxed"><AiMarkdown content={m.content} /><SourceCitations sources={sourcesMap[m.id] || []} /></div>}</div>;
      $[34] = sourcesMap;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    t18 = messages.map(t19);
    $[31] = messages;
    $[32] = sourcesMap;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[36] !== error || $[37] !== reload) {
    t19 = error && <div className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2.5"><AlertCircle className="mt-0.5 size-4 shrink-0 text-red-500" /><div><p className="text-sm font-medium text-red-500">Something went wrong</p><p className="text-xs text-fd-muted-foreground mt-0.5">{error.message || "Failed to get a response. Please try again."}</p><button onClick={() => reload()} className="mt-2 flex items-center gap-1.5 rounded-full border border-fd-border px-3 py-1 text-xs text-fd-muted-foreground hover:bg-fd-accent transition-colors"><RefreshCw className="size-3" />Retry</button></div></div>;
    $[36] = error;
    $[37] = reload;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== isLoading) {
    t20 = isLoading && <div role="status"><span className="text-[11px] font-medium text-fd-muted-foreground uppercase tracking-wide">RouterBench</span><div className="mt-1.5 flex items-center gap-2"><Loader2 className="size-4 animate-spin text-fd-muted-foreground" /><span className="text-xs text-fd-muted-foreground">{"Searching docs & generating answer..."}</span></div></div>;
    $[39] = isLoading;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div ref={messagesEndRef} />;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t22 = <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 space-y-4" role="log" aria-label="Chat conversation">{t17}{t18}{t19}{t20}{t21}</div>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== showScrollDown) {
    t23 = showScrollDown && <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-10"><button onClick={scrollToBottom} className="flex items-center gap-1 rounded-full border border-fd-border bg-fd-background px-3 py-1.5 text-xs text-fd-muted-foreground shadow-md hover:bg-fd-accent transition-colors"><ChevronDown className="size-3" />New messages</button></div>;
    $[47] = showScrollDown;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== clearChat || $[50] !== isLoading || $[51] !== messages || $[52] !== reload) {
    t24 = messages.length > 0 && <div className="flex items-center gap-2 px-4 pt-3">{!isLoading && messages.at(-1)?.role === "assistant" && <button onClick={() => reload()} className="flex items-center gap-1.5 rounded-full border border-fd-border px-3 py-1.5 text-xs text-fd-muted-foreground hover:bg-fd-accent hover:text-fd-accent-foreground transition-colors"><RefreshCw className="size-3" />Retry</button>}<button onClick={clearChat} className="flex items-center gap-1.5 rounded-full border border-fd-border px-3 py-1.5 text-xs text-fd-muted-foreground hover:bg-fd-accent hover:text-fd-accent-foreground transition-colors"><Trash2 className="size-3" />Clear</button></div>;
    $[49] = clearChat;
    $[50] = isLoading;
    $[51] = messages;
    $[52] = reload;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const t25 = isLoading ? "AI is answering..." : "Ask a question...";
  let t26;
  if ($[54] !== handleInputChange || $[55] !== input || $[56] !== isLoading || $[57] !== onKeyDown || $[58] !== t25) {
    t26 = <div className="flex-1 rounded-xl border border-fd-border bg-fd-secondary px-3 py-2"><textarea ref={inputRef} value={input} onChange={handleInputChange} onKeyDown={onKeyDown} placeholder={t25} rows={1} className="w-full resize-none bg-transparent text-sm outline-none placeholder:text-fd-muted-foreground min-h-[24px] max-h-[120px]" disabled={isLoading} aria-label="Ask a question about RouterBench" /></div>;
    $[54] = handleInputChange;
    $[55] = input;
    $[56] = isLoading;
    $[57] = onKeyDown;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== input || $[61] !== isLoading || $[62] !== stop) {
    t27 = isLoading ? <button type="button" onClick={() => stop()} className="mt-1 flex items-center gap-1.5 rounded-full border border-fd-border px-3 py-2 text-xs text-fd-muted-foreground hover:bg-fd-accent transition-colors" aria-label="Stop generating"><Loader2 className="size-3 animate-spin" />Stop</button> : <button type="submit" disabled={!input.trim()} className="mt-1 flex size-9 items-center justify-center rounded-full bg-fd-primary text-fd-primary-foreground disabled:opacity-30 transition-opacity" aria-label="Send message"><Send className="size-4" /></button>;
    $[60] = input;
    $[61] = isLoading;
    $[62] = stop;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== rawHandleSubmit || $[65] !== t26 || $[66] !== t27) {
    t28 = <form id="ai-chat-form" onSubmit={rawHandleSubmit} className="flex items-start gap-2 p-4">{t26}{t27}</form>;
    $[64] = rawHandleSubmit;
    $[65] = t26;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t24 || $[69] !== t28) {
    t29 = <div className="border-t border-fd-border">{t24}{t28}</div>;
    $[68] = t24;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== t22 || $[72] !== t23 || $[73] !== t29) {
    t30 = <div className="fixed top-0 right-0 z-50 h-full w-full max-w-full md:max-w-[480px] bg-fd-background border-l border-fd-border shadow-2xl flex flex-col">{t16}{t22}{t23}{t29}</div>;
    $[71] = t22;
    $[72] = t23;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  return t30;
}
function _temp22() {
  document.getElementById("ai-chat-form")?.dispatchEvent(new Event("submit", {
    bubbles: true,
    cancelable: true
  }));
}
function _temp21(o) {
  return !o;
}
function _temp20(err) {
  console.error("[Ask AI]", err);
}